/**
 * Client-side translation of server-action TRANSPORT failures (the PR
 * #1456/#1457/#1458 class): `await someAction()` rejects with an opaque
 * WebKit/Chromium string ("Load failed", "Failed to fetch", …) when the
 * request never completed — outlet WiFi, backgrounded tab, mid-deploy. Left
 * uncaught, the rejection escalates to the error boundary and tears down the
 * surface with the operator's typed state.
 *
 * Contract: translate ONLY the known transport strings into a plain,
 * actionable line; return null for everything else — digest-tagged server
 * errors and unrecognized throws must be RETHROWN by the caller so Next's
 * control flow and the error boundary keep working.
 */

const NETWORK_ERROR = /failed to fetch|load failed|networkerror|network request failed/i;
/**
 * "An unexpected response was received from the server." is NOT a network
 * failure: the server ANSWERED, wrongly — a 500, or mid-deploy version skew.
 * Folding it into the signal copy is how the 2026-08-26 /supply/ck incident
 * (every action POST 500ing at module evaluation) spent a day reported as
 * "connection lost": retrying on better wifi can never fix a server bug, and
 * nobody escalates what they believe is their own signal. Retry stays in the
 * copy (skew genuinely self-heals) but the blame moves to the server and the
 * escalation path is named — "tell HQ", the repo-standard escalation noun
 * (approvals/receive/order copy all use it; do not mint another).
 */
const SERVER_RESPONSE_ERROR = /unexpected response/i;

/** True when the throw carries a Next digest (server error / control flow) —
 *  never swallow these; rethrow so Next handles them. */
export function isDigestTagged(err: unknown): boolean {
  return (
    typeof err === "object" &&
    err !== null &&
    "digest" in err &&
    typeof (err as { digest?: unknown }).digest === "string"
  );
}

/**
 * One of two lines for a known transport-class failure — the signal copy for
 * genuine network strings, the server copy for "unexpected response" (a 500,
 * mid-deploy version skew, or — on surfaces that POST file bodies through a
 * server action — a body over the size limit, where "try once more" cannot
 * help and "tell HQ" is the whole value). Null for anything the caller must
 * rethrow (digest-tagged or unrecognized).
 */
export function describeTransportError(err: unknown): string | null {
  if (isDigestTagged(err)) return null;
  const raw = err instanceof Error ? err.message : "";
  if (SERVER_RESPONSE_ERROR.test(raw)) {
    return "The server had a problem — try once more. If it keeps happening, tell HQ: it is not your connection.";
  }
  if (NETWORK_ERROR.test(raw)) return "Check your signal and try again.";
  return null;
}

/**
 * True when the server ANSWERED (the "unexpected response" class) — the action
 * may well have committed before its reply came back unreadable (2026-10-01:
 * RQ-0421's Mark sent stamped at 11:18:38, the tablet still showed the error).
 * A caller re-reads with `router.refresh()` ONLY in this case: on a dead
 * connection Next's refresh fetch fails and falls back to a full browser
 * navigation, which swaps the "check your signal" line for the browser's
 * offline page.
 */
export function serverAnswered(err: unknown): boolean {
  if (isDigestTagged(err)) return false;
  return SERVER_RESPONSE_ERROR.test(err instanceof Error ? err.message : "");
}
